// external
import React, { FC, useMemo, useState } from 'react';
import { Box, Typography, Button, IconButton, Tooltip, Menu, MenuItem, Chip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import DeleteIcon from '@mui/icons-material/Delete';
import QrCodeIcon from '@mui/icons-material/QrCode2';

// internal
import useSnackbarHelper from '../../hooks/snackbar.hook';
import useService from '../../hooks/service.hook';
import EquipmentService from '../../services/equipments.service';
import EquipmentSharingService from '../../services/equipment-sharing.service';
import TransferEquipmentOwnershipDialog from '../dialogs/transfer-equipment-ownership.dialog';
import { palette } from '../../theme';
import { combineShoeSizeDisplay } from '../../helpers/equipment.helpers';
import { UserAvatar } from '../user-avatar/user-avatar.component';

interface EquipmentModalHeaderProps {
  equipment: any | null;
  selectedType: any | null;
  selectedSubtype: any | null;
  refreshParent: boolean;
  onClose: (refresh?: boolean) => void;
  onEquipmentUpdate?: (equipment: any) => void;
}

const normalizeTypeName = (name: string | undefined): string =>
  String(name ?? '')
    .trim()
    .toLowerCase();

const asDisplayValue = (v: unknown, fallback = '–'): string => {
  const out = String(v ?? '').trim();
  return out.length ? out : fallback;
};

const formatLabel = (key: string): string => key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

const extractFieldEntry = (raw: unknown): { value: string; unit: string } => {
  if (raw != null && typeof raw === 'object' && 'value' in raw) {
    const obj = raw as { value?: unknown; measurement_unit?: string | null };
    return {
      value: obj.value != null ? String(obj.value) : '',
      unit: obj.measurement_unit ?? '',
    };
  }
  return { value: raw != null ? String(raw) : '', unit: '' };
};

const getStandardValue = (map: Record<string, unknown>, key: string): string | null => {
  const raw = map[key] != null ? map[key] : Object.entries(map).find(([k]) => formatLabel(k) === key)?.[1];
  if (raw == null) return null;
  const { value, unit } = extractFieldEntry(raw);
  if (!value) return null;
  return unit ? `${value} ${unit}` : value;
};

type HeroHighlight = { label: string; value: string; isSnowBadge?: boolean };

const buildHeroHighlights = (typeName: string, standardFieldValues: Record<string, unknown>): HeroHighlight[] => {
  const skiLike = normalizeTypeName(typeName) === 'xc skis';
  const shoeLike = normalizeTypeName(typeName) === 'shoes';

  if (skiLike) {
    return [
      {
        label: 'Snow',
        value: asDisplayValue(getStandardValue(standardFieldValues, 'Snow Designation')),
        isSnowBadge: true,
      },
      { label: 'Grind', value: asDisplayValue(getStandardValue(standardFieldValues, 'Grind')) },
      { label: 'Base Material', value: asDisplayValue(getStandardValue(standardFieldValues, 'Base Material')) },
      { label: 'Length', value: asDisplayValue(getStandardValue(standardFieldValues, 'Ski Length in cm')) },
      { label: 'Camber', value: asDisplayValue(getStandardValue(standardFieldValues, 'Camber Height')) },
      { label: 'MF 0.2', value: asDisplayValue(getStandardValue(standardFieldValues, 'MF 0.2')) },
    ];
  }

  if (shoeLike) {
    return [
      { label: 'Stack Height', value: asDisplayValue(getStandardValue(standardFieldValues, 'Stack Height')) },
      { label: 'Drop', value: asDisplayValue(getStandardValue(standardFieldValues, 'Drop')) },
      { label: 'Cushioning', value: asDisplayValue(getStandardValue(standardFieldValues, 'Cushioning')) },
      { label: 'Terrain', value: asDisplayValue(getStandardValue(standardFieldValues, 'Terrain Type')) },
      { label: 'Weight', value: asDisplayValue(getStandardValue(standardFieldValues, 'Weight')) },
      {
        label: 'Size',
        value: (() => {
          const v = combineShoeSizeDisplay(standardFieldValues);
          return v === 'N/A' ? '–' : v;
        })(),
      },
    ];
  }

  return Array.from({ length: 6 }, () => ({ label: '', value: '–' }));
};

const getSnowBadgeSx = (value: string) => {
  const ref = value.toLowerCase();
  if (ref.includes('zero')) return { bgcolor: palette.divider, color: palette.text.primary };
  if (ref.includes('warm')) return { bgcolor: palette.error.dark, color: palette.background.paper };
  if (ref.includes('cold')) return { bgcolor: palette.primary.main, color: palette.background.paper };
  return { bgcolor: palette.primary.main, color: palette.background.paper };
};

const EquipmentModalHeader: FC<EquipmentModalHeaderProps> = (props) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferLoading, setTransferLoading] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);

  const equipmentService = useService(EquipmentService);
  const equipmentSharingService = useService(EquipmentSharingService);
  const { showSnackbar } = useSnackbarHelper();

  const equipmentId = props.equipment?.id;
  const typeName = props.selectedType?.name ?? props.equipment?.equipment_type?.name ?? '';
  const name = props.equipment?.name ?? '';
  const brand = props.equipment?.brand ?? '';
  const year = props.equipment?.year != null ? String(props.equipment.year) : '';
  const published = Boolean(props.equipment?.published);
  const standardFieldValues = props.equipment?.standard_field_values ?? {};
  const currentEquipment = props.equipment;
  const subtypeName = props.selectedSubtype?.name ?? '';
  const publicId = currentEquipment?.public_id ?? null;

  const highlights = useMemo(() => buildHeroHighlights(typeName, standardFieldValues), [typeName, standardFieldValues]);

  const subtitleParts = [brand, subtypeName, year].filter(Boolean);

  const handlePublish = (newPublished: boolean) => {
    if (!equipmentId) return;
    setPublishLoading(true);
    equipmentService
      .publishEquipmentV2(equipmentId, newPublished)
      .then(() => equipmentService.getEquipment(equipmentId))
      .then((res) => props.onEquipmentUpdate?.(res.data.data))
      .catch(() => {
        showSnackbar('Failed to update equipment public/private status. Please try again.', 'error');
      })
      .finally(() => setPublishLoading(false));
  };

  const handleDelete = () => {
    if (!equipmentId || !window.confirm('Are you sure you want to delete this equipment?')) return;
    equipmentService
      .deleteEquipment(equipmentId)
      .then(() => props.onClose(true))
      .catch(() => undefined);
  };

  const handleTransferSubmit = (email: string) => {
    if (!equipmentId) return;
    setTransferLoading(true);
    equipmentSharingService
      .sendEquipmentInvitation('owner_transfer', email, equipmentId)
      .then(() => {
        showSnackbar('Successfully sent ownership transfer invitation', 'success');
        setTransferDialogOpen(false);
      })
      .catch((err) => {
        let errorMessage = 'Failed to send ownership transfer invitation. Please try again later';
        if (err?.response?.status === 400) errorMessage = err?.response?.data?.message || 'Invalid email.';
        if (err?.response?.status === 403) errorMessage = 'You do not have permission to transfer ownership.';
        if (err?.response?.status === 404) errorMessage = 'Equipment not found.';
        showSnackbar(errorMessage, 'error');
      })
      .finally(() => setTransferLoading(false));
  };

  return (
    <>
      {/* Hero section */}
      <Box sx={{ bgcolor: palette.background.subtle, borderRadius: '20px 20px 0 0', px: 2, pt: 2, pb: 0 }}>
        {/* Top row: QR code/icon + name/subtitle | actions */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
          {/* Left: QR + title block */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, minWidth: 0, flex: 1 }}>
            <Box sx={{ flexShrink: 0, mt: 0.25 }}>
              {published && equipmentId ? (
                <Box
                  component="img"
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=56x56&data=${encodeURIComponent(
                    `${window.location.origin}/equip/${currentEquipment?.public_id ?? equipmentId}`
                  )}`}
                  alt="QR Code"
                  sx={{
                    width: 48,
                    height: 48,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '6px',
                    display: 'block',
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px dashed',
                    borderColor: 'divider',
                    borderRadius: '6px',
                    color: 'text.disabled',
                  }}
                >
                  <QrCodeIcon sx={{ fontSize: 28 }} />
                </Box>
              )}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h5"
                sx={{ fontWeight: 700, lineHeight: 1.25, color: palette.text.primary, wordBreak: 'break-word' }}
                id="equipment-modal-title"
              >
                {name || (equipmentId ? 'Equipment' : '—')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '2px 4px', mt: 0.25 }}>
                {subtitleParts.map((part, i) => (
                  <React.Fragment key={part}>
                    {i > 0 && (
                      <Typography component="span" sx={{ color: palette.text.disabled, fontSize: 12 }}>
                        ·
                      </Typography>
                    )}
                    <Typography
                      component="span"
                      sx={{ fontSize: 12, color: i === 0 ? palette.primary.main : palette.text.secondary }}
                    >
                      {part}
                    </Typography>
                  </React.Fragment>
                ))}
                {publicId != null && (
                  <>
                    {subtitleParts.length > 0 && (
                      <Typography component="span" sx={{ color: palette.text.disabled, fontSize: 12 }}>
                        ·
                      </Typography>
                    )}
                    <Typography
                      component="span"
                      sx={{ fontSize: 11, color: palette.text.disabled, fontFamily: 'monospace' }}
                    >
                      #{String(publicId).slice(0, 8).toUpperCase()}
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          </Box>

          {/* Right: kebab + close */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            {equipmentId && currentEquipment?.is_owner === true && (
              <Tooltip title="Options" disableInteractive>
                <IconButton onClick={(e) => setMenuAnchorEl(e.currentTarget)} size="small">
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Close" disableInteractive>
              <IconButton onClick={() => props.onClose(props.refreshParent)} size="small">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Hero highlights strip */}
        <Box sx={{ display: 'flex', gap: 0.75, mb: 1 }}>
          {highlights.map((hl, i) => (
            <Box
              key={i}
              sx={{
                flex: 1,
                bgcolor: (t) => alpha(t.palette.grey[500], 0.05),
                borderRadius: 1,
                px: 1,
                py: 0.75,
                minWidth: 0,
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box sx={{ mb: 0.25, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {hl.isSnowBadge && hl.value !== '–' ? (
                    <Chip
                      label={hl.value}
                      size="small"
                      sx={{ fontSize: 11, height: 20, fontWeight: 700, ...getSnowBadgeSx(hl.value) }}
                    />
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 800,
                        fontSize: '0.9375rem',
                        lineHeight: 1.25,
                        color: hl.value === '–' ? 'text.secondary' : 'text.primary',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        textAlign: 'center',
                        letterSpacing: -0.2,
                      }}
                    >
                      {hl.value}
                    </Typography>
                  )}
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 400,
                    letterSpacing: 0.5,
                    textTransform: 'uppercase',
                    fontSize: '0.575rem',
                    lineHeight: 1.2,
                    textAlign: 'center',
                    display: 'block',
                  }}
                >
                  {hl.label || '\u00A0'}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Athlete reference strip */}
        {equipmentId && currentEquipment && (
          <Box
            sx={{
              bgcolor: (t) => alpha(t.palette.grey[500], 0.1),
              border: `1px solid ${palette.divider}`,
              // borderBottom: 'none',
              borderRadius: '10px',
              px: 1.5,
              py: 0.75,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            {/* Avatar + name/email */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
              <UserAvatar
                src={currentEquipment.owner?.profile_image}
                firstname={currentEquipment.owner?.firstname}
                lastname={currentEquipment.owner?.lastname}
                userId={currentEquipment.owner?.id}
                size={30}
                fontSize="0.7rem"
              />
              {currentEquipment.owner && typeof currentEquipment.owner === 'object' ? (
                <Box>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: palette.text.primary, lineHeight: 1.2 }}>
                    {[currentEquipment.owner.firstname, currentEquipment.owner.lastname].filter(Boolean).join(' ') ||
                      '—'}
                  </Typography>
                  {currentEquipment.owner.email && (
                    <Typography sx={{ fontSize: 10, color: palette.text.secondary, lineHeight: 1.2 }}>
                      {currentEquipment.owner.email}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography sx={{ fontSize: 12, color: palette.text.secondary }}>No owner</Typography>
              )}
            </Box>

            {/* Stat cells with vertical dividers */}
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, justifyContent: 'flex-end' }}>
              {[
                {
                  label: 'Gender',
                  value: currentEquipment.owner?.sex
                    ? String(currentEquipment.owner.sex).charAt(0).toUpperCase() +
                      String(currentEquipment.owner.sex).slice(1)
                    : '–',
                },
                { label: 'Height', value: '–' },
                { label: 'Weight', value: '–' },
              ].map((stat, i) => (
                <React.Fragment key={stat.label}>
                  {i > 0 && <Box sx={{ width: '1px', height: 28, bgcolor: palette.divider, flexShrink: 0, mx: 1 }} />}
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: palette.text.primary, lineHeight: 1.3 }}>
                      {stat.value}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 9,
                        fontWeight: 600,
                        color: palette.text.secondary,
                        lineHeight: 1.2,
                        textTransform: 'uppercase',
                        letterSpacing: 0.3,
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </Box>
                </React.Fragment>
              ))}
            </Box>

            {/* Privatize / Publicize button */}
            {currentEquipment.is_owner === true && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => handlePublish(!published)}
                disabled={publishLoading}
                sx={{
                  flexShrink: 0,
                  fontSize: 11,
                  py: 0.4,
                  px: 1.25,
                  minWidth: 0,
                  color: palette.text.primary,
                  borderColor: palette.divider,
                  bgcolor: palette.grey[50],
                  borderRadius: '8px',
                  '&:hover': { borderColor: palette.text.secondary },
                  textTransform: 'none',
                }}
              >
                {published ? 'Privatize' : 'Publicize'}
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* Kebab menu */}
      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={() => setMenuAnchorEl(null)}>
        {currentEquipment?.is_owner === true && (
          <MenuItem
            onClick={() => {
              setMenuAnchorEl(null);
              setTransferDialogOpen(true);
            }}
          >
            <SwapHorizIcon sx={{ mr: 1, fontSize: 18 }} />
            Transfer Ownership
          </MenuItem>
        )}
        {currentEquipment?.is_owner === true && (
          <MenuItem
            onClick={() => {
              setMenuAnchorEl(null);
              handleDelete();
            }}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon sx={{ mr: 1, fontSize: 18 }} />
            Delete Equipment
          </MenuItem>
        )}
      </Menu>

      {equipmentId && currentEquipment?.is_owner === true && (
        <TransferEquipmentOwnershipDialog
          isOpen={transferDialogOpen}
          equipmentName={currentEquipment.name}
          handleClose={() => setTransferDialogOpen(false)}
          handleSubmit={handleTransferSubmit}
          isLoading={transferLoading}
        />
      )}
    </>
  );
};

export default EquipmentModalHeader;
